import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Collapse,
    IconButton,
    MenuItem,
    SvgIcon,
    Switch,
    TextField,
    Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import {
    configureTenantAuthProvider,
    createTenantAuthProvider,
    deleteTenantAuthProvider,
    fetchTenantAuthProviders,
    fetchTenantAuthProviderSpConfig,
    fetchTenantAuthProviderSpMetadataXml,
    testTenantAuthProviderSso,
    updateTenantAuthProvider
} from "../../../api/admin/tenant/endpoints-referrers";
import type { EmbedProviderType } from "../../../common/interfaces/admin/integration.interface";
import type { TenantAuthProvider } from "../../../common/interfaces/admin/tenant.interface";
import SsoProviderConfig, {
    SsoProviderConfigApi
} from "../../../components/admin/sso-provider-config/sso-provider-config.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";

export interface TenantSsoProvidersProps {
    companyId: number;
    /** Whether the SSO sign-in method is currently on in the (unsaved) form. */
    ssoMethodEnabled: boolean;
    /** Lets the tab gate the SSO checkbox on "at least one enabled provider". */
    onProvidersChanged(providers: TenantAuthProvider[]): void;
}

type ProviderStatus = "unconfigured" | "untested" | "failed" | "ready" | "enabled";

const statusOf = (p: TenantAuthProvider): ProviderStatus => {
    if (p.isEnabled) return "enabled";
    if (!p.providerConfiguredAt) return "unconfigured";
    if (p.lastSsoTestOutcome === "passed") return "ready";
    if (p.lastSsoTestOutcome === "failed") return "failed";
    return "untested";
};

const STATUS_LABEL: Record<ProviderStatus, string> = {
    unconfigured: "Not configured",
    untested: "Not tested",
    failed: "Test failed",
    ready: "Ready to enable",
    enabled: "Enabled"
};

/** The embed host (`chat.<env>`) is the sibling of this admin subdomain. */
const chatOriginForHere = (): string | null => {
    const parts = window.location.hostname.split(".");
    if (parts.length < 3) return null;
    return `${window.location.protocol}//chat.${parts.slice(1).join(".")}`;
};

function ProviderRow({
    companyId,
    provider,
    ssoMethodEnabled,
    onChange,
    onDelete
}: {
    companyId: number;
    provider: TenantAuthProvider;
    ssoMethodEnabled: boolean;
    onChange(updated: TenantAuthProvider): void;
    onDelete(): void;
}) {
    const [open, setOpen] = useState(!provider.providerConfiguredAt);
    const [nameDraft, setNameDraft] = useState(provider.displayName);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>("");
    const status = statusOf(provider);

    useEffect(() => setNameDraft(provider.displayName), [provider.displayName]);

    const api = useMemo<SsoProviderConfigApi>(
        () => ({
            fetchSpConfig: () => fetchTenantAuthProviderSpConfig(companyId, provider.id),
            fetchSpMetadataXml: () => fetchTenantAuthProviderSpMetadataXml(companyId, provider.id),
            setProvider: body => configureTenantAuthProvider(companyId, provider.id, body),
            testSso: idToken => testTenantAuthProviderSso(companyId, provider.id, idToken)
        }),
        [companyId, provider.id]
    );

    const patch = async (data: { displayName?: string; isEnabled?: boolean }) => {
        setBusy(true);
        setError("");
        try {
            const { data: updated } = await updateTenantAuthProvider(companyId, provider.id, data);
            onChange(updated);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? "Saving the provider failed.");
        } finally {
            setBusy(false);
        }
    };

    const commitName = () => {
        const name = nameDraft.trim();
        if (!name || name === provider.displayName) {
            setNameDraft(provider.displayName);
            return;
        }
        void patch({ displayName: name });
    };

    const canEnable = status === "ready";
    const switchTitle = provider.isEnabled
        ? ssoMethodEnabled
            ? "Offered on the login page. Turn SSO off under Sign-in methods before disabling the last enabled provider."
            : "Enabled; turn on SSO under Sign-in methods to offer it."
        : canEnable
          ? "Offer this provider on the login page"
          : "A passing sign-in test is required before enabling";

    return (
        <li className={`tenant-sso__row tenant-sso__row--${status}`}>
            <div className="tenant-sso__summary">
                <IconButton
                    size="small"
                    aria-label={
                        open
                            ? `Collapse ${provider.displayName}`
                            : `Configure ${provider.displayName}`
                    }
                    aria-expanded={open}
                    onClick={() => setOpen(o => !o)}
                >
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <span className="tenant-sso__name">{provider.displayName}</span>
                <span className="tenant-sso__type">{provider.providerType.toUpperCase()}</span>
                <span className={`tenant-sso__status tenant-sso__status--${status}`}>
                    {STATUS_LABEL[status]}
                </span>
                <Tooltip title={switchTitle}>
                    <span>
                        <Switch
                            size="small"
                            checked={provider.isEnabled}
                            disabled={busy || (!provider.isEnabled && !canEnable)}
                            onChange={e => void patch({ isEnabled: e.target.checked })}
                            inputProps={{ "aria-label": `Enable ${provider.displayName}` }}
                        />
                    </span>
                </Tooltip>
                <Tooltip title="Remove provider">
                    <IconButton
                        size="small"
                        aria-label={`Remove ${provider.displayName}`}
                        onClick={onDelete}
                        disabled={busy}
                    >
                        <SvgIcon component={TrashIcon} inheritViewBox fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>
            {error && (
                <p className="tenant-permissions__hint tenant-sso__error" role="alert">
                    {error}
                </p>
            )}
            <Collapse in={open} unmountOnExit>
                <div className="tenant-sso__details">
                    <TextField
                        label="Button label"
                        helperText={`Shown on the login page as "Continue with ${nameDraft.trim() || provider.displayName}"`}
                        value={nameDraft}
                        onChange={e => setNameDraft(e.target.value)}
                        onBlur={commitName}
                        onKeyDown={e => {
                            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        }}
                        size="small"
                        inputProps={{ maxLength: 60 }}
                        disabled={busy}
                    />
                    <SsoProviderConfig
                        providerType={provider.providerType as EmbedProviderType}
                        state={provider}
                        chatOrigin={chatOriginForHere()}
                        metadataFileName={`sp-metadata-${provider.displayName.replace(/\W+/g, "-").toLowerCase()}.xml`}
                        api={api}
                        onConfigured={updated => onChange(updated as TenantAuthProvider)}
                        onTested={result =>
                            onChange({
                                ...provider,
                                lastSsoTestAt: result.testedAt,
                                lastSsoTestOutcome: result.outcome
                            })
                        }
                        passedHint="This provider passed its test and can be enabled with the switch in its row."
                        pendingHint="A passing test is required before this provider can be enabled."
                    />
                </div>
            </Collapse>
        </li>
    );
}

/**
 * "SSO providers" group of the Authentication tab (tenant_authentication_plan.md
 * §2.4, Phase 4): the tenant's SAML/OIDC providers with add, configure, test,
 * enable and remove. Provider rows are saved immediately by their own
 * endpoints — they are not part of the tab's Save; only the SSO sign-in
 * method checkbox is.
 */
export default function TenantSsoProviders({
    companyId,
    ssoMethodEnabled,
    onProvidersChanged
}: TenantSsoProvidersProps) {
    const [providers, setProviders] = useState<TenantAuthProvider[] | null>(null);
    const [loadError, setLoadError] = useState<string>("");
    const [newType, setNewType] = useState<EmbedProviderType>("saml");
    const [newName, setNewName] = useState<string>("");
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState<string>("");
    const [pendingDelete, setPendingDelete] = useState<TenantAuthProvider | null>(null);
    const [deleteError, setDeleteError] = useState<string>("");

    const publish = useCallback(
        (next: TenantAuthProvider[]) => {
            setProviders(next);
            onProvidersChanged(next);
        },
        [onProvidersChanged]
    );

    useEffect(() => {
        let cancelled = false;
        setProviders(null);
        setLoadError("");
        fetchTenantAuthProviders(companyId)
            .then(({ data }) => {
                if (!cancelled) publish(data);
            })
            .catch(err => {
                if (!cancelled)
                    setLoadError(err?.response?.data?.message ?? "Couldn't load SSO providers.");
            });
        return () => {
            cancelled = true;
        };
    }, [companyId, publish]);

    const add = async () => {
        const name = newName.trim();
        if (!name) {
            setAddError("Enter the button label first.");
            return;
        }
        setAdding(true);
        setAddError("");
        try {
            const { data } = await createTenantAuthProvider(companyId, {
                providerType: newType,
                displayName: name
            });
            publish([...(providers ?? []), data]);
            setNewName("");
        } catch (err: any) {
            setAddError(err?.response?.data?.message ?? "Adding the provider failed.");
        } finally {
            setAdding(false);
        }
    };

    const remove = async () => {
        if (!pendingDelete) return;
        const target = pendingDelete;
        setPendingDelete(null);
        setDeleteError("");
        try {
            await deleteTenantAuthProvider(companyId, target.id);
            publish((providers ?? []).filter(p => p.id !== target.id));
        } catch (err: any) {
            setDeleteError(err?.response?.data?.message ?? "Removing the provider failed.");
        }
    };

    return (
        <div className="tenant-permissions__group tenant-sso">
            <div className="tenant-permissions__group-title" role="heading" aria-level={3}>
                SSO providers
            </div>
            <p className="tenant-permissions__hint">
                Identity providers offered on the login page as &ldquo;Continue with
                &lt;name&gt;&rdquo;. Add one, give its SP details to the IdP team, enter the IdP
                details, run the sign-in test, then enable it. The SSO sign-in method above can be
                switched on once a provider is enabled. Changes here save immediately.
            </p>
            {loadError && (
                <p className="tenant-permissions__hint" role="alert">
                    {loadError}
                </p>
            )}
            {deleteError && (
                <p className="tenant-permissions__hint" role="alert">
                    {deleteError}
                </p>
            )}
            {providers && providers.length > 0 && (
                <ul className="tenant-sso__list" aria-label="SSO providers">
                    {providers.map(p => (
                        <ProviderRow
                            key={p.id}
                            companyId={companyId}
                            provider={p}
                            ssoMethodEnabled={ssoMethodEnabled}
                            onChange={updated =>
                                publish(providers.map(x => (x.id === updated.id ? updated : x)))
                            }
                            onDelete={() => setPendingDelete(p)}
                        />
                    ))}
                </ul>
            )}
            {providers && providers.length === 0 && (
                <p className="tenant-permissions__hint">No SSO providers yet.</p>
            )}
            <div className="tenant-sso__add">
                <TextField
                    select
                    label="Type"
                    size="small"
                    value={newType}
                    onChange={e => setNewType(e.target.value as EmbedProviderType)}
                    className="tenant-sso__add-type"
                >
                    <MenuItem value="saml">SAML</MenuItem>
                    <MenuItem value="oidc">OIDC</MenuItem>
                </TextField>
                <TextField
                    label="Button label"
                    size="small"
                    placeholder="e.g. Okta, Acme SSO"
                    value={newName}
                    onChange={e => {
                        setNewName(e.target.value);
                        setAddError("");
                    }}
                    onKeyDown={e => {
                        if (e.key === "Enter") void add();
                    }}
                    inputProps={{ maxLength: 60 }}
                    className="tenant-sso__add-name"
                />
                <Button variant="outlined" onClick={() => void add()} disabled={adding}>
                    {adding ? "Adding…" : "Add provider"}
                </Button>
            </div>
            {addError && (
                <p className="tenant-permissions__hint" role="alert">
                    {addError}
                </p>
            )}
            <ConfirmationPopup
                open={!!pendingDelete}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Remove SSO provider"
                subtitle={pendingDelete?.displayName}
                description={
                    pendingDelete?.firebaseProviderId
                        ? "The provider configuration is deleted from the tenant's Firebase tenant; users who signed in through it will need another method. This can't be undone."
                        : "This provider has no IdP details yet; only the entry is removed."
                }
                actionName="Remove"
                handleAction={() => void remove()}
                handleClose={() => setPendingDelete(null)}
            />
        </div>
    );
}
